import {ComponentProps, MouseEventHandler, VFC, useState} from 'react';
import {EzButton, EzModal} from '@ezcater/recipe';
import {useFlashMessages} from '@ezcater/flash-messages';
import {ClassNames} from '@emotion/react';
import {useMutation} from 'services/apollo';
import {DmDeliveryEventTypes, DmDeliveryStatus} from 'services/graphql/types';
import type {
  CreateDeliveryEventMutation,
  CreateDeliveryEventMutationVariables,
} from 'services/graphql/types';
import {CreateDeliveryEventQuery} from 'services/graphql';
import styled from '@emotion/styled';
import {useTranslation} from 'react-i18next';
import {trackButtonClick} from 'util/tracking';
import StartDeliveryButton from './StartDeliveryButton';
import CompleteDeliveryButton from './CompleteDeliveryButton';

const Container = styled.div`
  background-color: #fff;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);
  margin-top: 1rem;
  padding: 1rem 1.5rem 1rem 1.5rem;

  & > * {
    height: 100%;
    width: 100%;
    padding: 0.5rem 0 0.5rem 0;
  }
`;

interface Props {
  readonly eventAt: string;
  readonly driverAssignmentId: string;
  readonly status: `${DmDeliveryStatus}`;
}

// Map to relate the status to the
const STATUS_MAP = {
  [DmDeliveryStatus.Assigned]: DmDeliveryEventTypes.DriverPickedUp,
  [DmDeliveryStatus.InProgress]: DmDeliveryEventTypes.DriverDroppedOff,
} as const;

function isValidStatus(x: string): x is keyof typeof STATUS_MAP {
  return x in STATUS_MAP;
}

type PrimaryButtonProps = Omit<ComponentProps<typeof EzButton>, 'use'>;

type ButtonProps = Pick<Props, 'driverAssignmentId'> &
  PrimaryButtonProps & {
    /**
     * The status to transition to next.
     */
    readonly nextStatus: (typeof STATUS_MAP)[keyof typeof STATUS_MAP];
    /**
     * The current status
     */
    readonly status: keyof typeof STATUS_MAP;
    readonly eventAt: string;
  };

const UpdateDeliveryButton: VFC<ButtonProps> = ({
  driverAssignmentId,
  status,
  nextStatus,
  eventAt,
}) => {
  const {t} = useTranslation();
  const errorLabel = t('driverProgress.actionButtons.error');
  const uncaughtErrorMessage = t('driverProgress.actionButtons.uncaughtError');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalIsSubmitting, setModalIsSubmitting] = useState(false);
  const {addFlashMessage} = useFlashMessages();
  const [updateDelivery, {loading}] = useMutation<
    CreateDeliveryEventMutation,
    CreateDeliveryEventMutationVariables
  >(CreateDeliveryEventQuery, {
    variables: {
      input: {
        driverAssignmentId,
        eventType: nextStatus,
      },
    },
    onCompleted(result) {
      result?.createDeliveryEvent?.userErrors?.forEach(error => {
        addFlashMessage({
          type: 'error',
          headline: errorLabel,
          message: error.message,
        });
      });
    },
    onError() {
      addFlashMessage({
        type: 'error',
        headline: errorLabel,
        message: uncaughtErrorMessage,
      });
    },
  });

  const handleStartDelivery: MouseEventHandler<HTMLButtonElement> = async () => {
    await updateDelivery();

    trackButtonClick(`${nextStatus}-button`.replace(/_/g, '-'), {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
  };

  const handleModalOpen = () => {
    setModalIsOpen(true);
  };

  const handleCompleteDelivery = async () => {
    await updateDelivery();

    trackButtonClick(`${nextStatus}-button`.replace(/_/g, '-'), {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
    setModalIsSubmitting(false);
    setModalIsOpen(false);
  };

  const handleModalDismiss = async () => {
    setModalIsOpen(false);

    trackButtonClick('complete-delivery-modal-dismiss-button', {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
  };

  if (status === DmDeliveryStatus.Assigned) {
    return (
      <StartDeliveryButton loading={loading} eventAt={eventAt} onClick={handleStartDelivery} />
    );
  }

  if (status === DmDeliveryStatus.InProgress) {
    const confirmationText = t('driverProgress.completeDelivery.modal.confirmationMessage');
    const confirmLabel = t('driverProgress.completeDelivery.modal.confirm');
    const cancelLabel = t('driverProgress.completeDelivery.modal.cancel');
    const header = t('driverProgress.completeDelivery.modal.header');

    return (
      <>
        <ClassNames>
          {({css}) => (
            <EzModal
              slotProps={{
                dialog: {
                  className: css`
                    @media (max-width: 768px) {
                      height: 50%;
                      width: 90%;
                    }
                  `,
                },
              }}
              isSubmitting={modalIsSubmitting}
              isOpen={modalIsOpen}
              onSubmit={() => handleCompleteDelivery()}
              submitLabel={confirmLabel}
              onDismiss={() => handleModalDismiss()}
              dismissLabel={cancelLabel}
              headerText={header}
            >
              {confirmationText}
            </EzModal>
          )}
        </ClassNames>
        <CompleteDeliveryButton loading={loading} onClick={handleModalOpen} />
      </>
    );
  }

  return null;
};

/**
 * Provides the appropriate action given the current state of some order
 * If no action is available for a given state, returns null
 *
 * @param param0
 * @returns
 */
const ActionButton: VFC<Props> = ({driverAssignmentId, status, eventAt}) => {
  if (!isValidStatus(status)) {
    return null;
  }

  const nextStatus = STATUS_MAP[status];

  return (
    <Container>
      <UpdateDeliveryButton
        status={status}
        nextStatus={nextStatus}
        eventAt={eventAt}
        driverAssignmentId={driverAssignmentId}
      />
    </Container>
  );
};

export default ActionButton;
